import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import ChunkedFileUpload from './ChunkedFileUpload';
import MediaUploadWithMeta from './MediaUploadWithMeta';
import AISeoSuggestions from './AISeoSuggestions';
import MediaDisplay from '@/components/media/MediaDisplay';

const ACCEPTED_FIELDS_KEY = (projectId) => `seo_accepted_${projectId}`;

const loadAcceptedFields = (projectId) => {
  if (!projectId || projectId === 'new') return {};
  try { return JSON.parse(localStorage.getItem(ACCEPTED_FIELDS_KEY(projectId)) || '{}'); } catch { return {}; }
};

const saveAcceptedFields = (projectId, fields) => {
  if (!projectId || projectId === 'new') return;
  localStorage.setItem(ACCEPTED_FIELDS_KEY(projectId), JSON.stringify(fields));
};

export default function ProjectsManager() {
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [acceptedFields, setAcceptedFields] = useState({});
  const [activeUploads, setActiveUploads] = useState(0);
  const queryClient = useQueryClient();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => base44.entities.Project.list('-updated_date'),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Project.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setEditing(null);
      setFormData({});
      setActiveUploads(0);
      toast.success('Progetto creato');
    },
    onError: (error) => toast.error(error?.message || 'Impossibile creare il progetto'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Project.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setEditing(null);
      setFormData({});
      setActiveUploads(0);
      toast.success('Progetto aggiornato');
    },
    onError: (error) => toast.error(error?.message || 'Impossibile salvare il progetto'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Project.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Progetto eliminato');
    },
  });

  const handleEdit = (project) => {
    setEditing(project.id);
    setFormData(project);
    setAcceptedFields(loadAcceptedFields(project.id));
    setActiveUploads(0);
  };

  const handleNew = () => {
    setEditing('new');
    setAcceptedFields({});
    setActiveUploads(0);
    setFormData({
      title: '',
      category: 'Residenziale',
      year: '',
      location: '',
      client: '',
      area: '',
      photographer: '',
      cover_media_type: 'image',
      cover_image: '',
      cover_video_url: '',
      cover_image_meta: {},
      gallery_images: [],
      gallery_media_types: [],
      gallery_images_meta: [],
      description: '',
      short_description: '',
      featured: false,
      client_video_meta: {},
      glb_file_data: null
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeUploads > 0) {
      toast.error('Attendi il completamento del caricamento delle immagini');
      return;
    }
    if (editing === 'new') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id: editing, data: formData });
    }
  };

  const handleSimpleUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setFormData(prev => ({ ...prev, [field]: file_url }));
      toast.success('File caricato');
    } catch {
      toast.error('Errore nel caricamento');
    }
    e.target.value = '';
  };

  const updateGalleryImage = (idx, url) => {
    setFormData(prev => {
      const gallery_images = [...(prev.gallery_images || [])];
      gallery_images[idx] = url;
      return { ...prev, gallery_images };
    });
  };

  const updateGalleryMeta = (idx, meta) => {
    setFormData(prev => {
      const gallery_images_meta = [...(prev.gallery_images_meta || [])];
      gallery_images_meta[idx] = meta;
      return { ...prev, gallery_images_meta };
    });
  };

  const updateGalleryType = (idx, mediaType) => {
    setFormData(prev => {
      const gallery_media_types = [...(prev.gallery_media_types || [])];
      gallery_media_types[idx] = mediaType;
      return { ...prev, gallery_media_types };
    });
  };

  const addGalleryImage = () => {
    setFormData(prev => ({
      ...prev,
      gallery_images: [...(prev.gallery_images || []), ''],
      gallery_media_types: [...(prev.gallery_media_types || []), 'image'],
      gallery_images_meta: [...(prev.gallery_images_meta || []), {}],
    }));
  };

  const removeGalleryImage = (idx) => {
    const imgs = [...(formData.gallery_images || [])];
    const types = [...(formData.gallery_media_types || [])];
    const metas = [...(formData.gallery_images_meta || [])];
    imgs.splice(idx, 1);
    types.splice(idx, 1);
    metas.splice(idx, 1);
    setFormData(prev => ({ ...prev, gallery_images: imgs, gallery_media_types: types, gallery_images_meta: metas }));
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{editing === 'new' ? 'Nuovo Progetto' : 'Modifica Progetto'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Titolo *</Label>
                <Input
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Categoria *</Label>
                <Select
                  value={formData.category || 'Residenziale'}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Residenziale">Residenziale</SelectItem>
                    <SelectItem value="Commerciale">Commerciale</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Outdoor">Outdoor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anno</Label>
                <Input value={formData.year || ''} onChange={(e) => setFormData({ ...formData, year: e.target.value })} />
              </div>
              <div>
                <Label>Luogo</Label>
                <Input value={formData.location || ''} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
              </div>
              <div>
                <Label>Cliente</Label>
                <Input value={formData.client || ''} onChange={(e) => setFormData({ ...formData, client: e.target.value })} />
              </div>
              <div>
                <Label>Superficie (mq)</Label>
                <Input value={formData.area || ''} onChange={(e) => setFormData({ ...formData, area: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Fotografo</Label>
                <Input value={formData.photographer || ''} onChange={(e) => setFormData({ ...formData, photographer: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Descrizione Breve</Label>
              <Textarea
                value={formData.short_description || ''}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Descrizione Completa</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>

            {/* Cover media with SEO meta */}
            <MediaUploadWithMeta
              key={`cover-${editing}`}
              label="Copertina progetto *"
              fieldId={`cover-${editing}`}
              type={formData.cover_media_type || 'image'}
              url={(formData.cover_media_type || 'image') === 'video' ? formData.cover_video_url || '' : formData.cover_image || ''}
              meta={formData.cover_image_meta || {}}
              onTypeChange={(cover_media_type) => setFormData(prev => ({ ...prev, cover_media_type }))}
              onChange={(url) => setFormData(prev => (prev.cover_media_type || 'image') === 'video' ? { ...prev, cover_video_url: url } : { ...prev, cover_image: url })}
              onMetaChange={(cover_image_meta) => setFormData(prev => ({ ...prev, cover_image_meta }))}
              onUploadingChange={(uploading) => setActiveUploads(count => Math.max(0, count + (uploading ? 1 : -1)))}
            />

            {/* Gallery images with SEO meta */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Galleria immagini e video</Label>
                <Button type="button" variant="outline" size="sm" onClick={addGalleryImage}>
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi contenuto
                </Button>
              </div>
              {(formData.gallery_images || []).map((img, idx) => {
                const galleryMeta = (formData.gallery_images_meta || [])[idx] || {};
                const galleryType = (formData.gallery_media_types || [])[idx] || 'image';
                return (
                <div key={idx} className="relative">
                  <MediaUploadWithMeta
                    key={`gallery-${editing}-${idx}`}
                    label={`Contenuto galleria ${idx + 1}`}
                    fieldId={`gallery-${editing}-${idx}`}
                    type={galleryType}
                    url={img}
                    meta={galleryMeta}
                    onTypeChange={(mediaType) => updateGalleryType(idx, mediaType)}
                    onChange={(url) => updateGalleryImage(idx, url)}
                    onMetaChange={(meta) => updateGalleryMeta(idx, meta)}
                    onUploadingChange={(uploading) => setActiveUploads(count => Math.max(0, count + (uploading ? 1 : -1)))}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => removeGalleryImage(idx)}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" /> Elimina contenuto
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>

            {/* GLB file */}
            <div>
              <Label>File GLB per Visione 3D (opzionale)</Label>
              <ChunkedFileUpload
                accept=".glb,.gltf"
                label="Upload GLB File (supporta file grandi)"
                onUploadComplete={(data) => setFormData({ ...formData, glb_file_data: data })}
              />
              {formData.glb_file_data?.chunked && (
                <p className="text-xs text-[#7a7a7a] mt-2">File GLB chunked: {formData.glb_file_data.fileName} ({formData.glb_file_data.partUrls.length} parti)</p>
              )}
              {formData.glb_file_data && !formData.glb_file_data.chunked && (
                <p className="text-xs text-[#7a7a7a] mt-2">File GLB: {formData.glb_file_data.fileName}</p>
              )}
            </div>

            {/* Plans */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Planimetria Prima</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.before_plan || ''}
                    onChange={(e) => setFormData({ ...formData, before_plan: e.target.value })}
                    placeholder="URL planimetria"
                  />
                  <Button type="button" variant="outline" onClick={() => document.getElementById('before-plan-upload').click()}>
                    <Upload className="w-4 h-4" />
                  </Button>
                  <input id="before-plan-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleSimpleUpload(e, 'before_plan')} />
                </div>
                {formData.before_plan && <img src={formData.before_plan} alt="Prima" className="mt-2 h-32 object-cover rounded" />}
              </div>
              <div>
                <Label>Planimetria Dopo</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.after_plan || ''}
                    onChange={(e) => setFormData({ ...formData, after_plan: e.target.value })}
                    placeholder="URL planimetria"
                  />
                  <Button type="button" variant="outline" onClick={() => document.getElementById('after-plan-upload').click()}>
                    <Upload className="w-4 h-4" />
                  </Button>
                  <input id="after-plan-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleSimpleUpload(e, 'after_plan')} />
                </div>
                {formData.after_plan && <img src={formData.after_plan} alt="Dopo" className="mt-2 h-32 object-cover rounded" />}
              </div>
            </div>

            {/* Moodboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Immagine Moodboard</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.moodboard_image || ''}
                    onChange={(e) => setFormData({ ...formData, moodboard_image: e.target.value })}
                    placeholder="URL immagine moodboard"
                  />
                  <Button type="button" variant="outline" onClick={() => document.getElementById('moodboard-upload').click()}>
                    <Upload className="w-4 h-4" />
                  </Button>
                  <input id="moodboard-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleSimpleUpload(e, 'moodboard_image')} />
                </div>
                {formData.moodboard_image && <img src={formData.moodboard_image} alt="Moodboard" className="mt-2 h-32 object-cover rounded" />}
              </div>
              <div>
                <Label>Testo Moodboard</Label>
                <Textarea
                  value={formData.moodboard_text || ''}
                  onChange={(e) => setFormData({ ...formData, moodboard_text: e.target.value })}
                  rows={4}
                  placeholder="Descrizione della moodboard..."
                />
              </div>
            </div>

            {/* Client video */}
            <div>
              <Label>Video Cliente</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.client_video_url || ''}
                  onChange={(e) => setFormData({ ...formData, client_video_url: e.target.value })}
                  placeholder="URL video cliente"
                />
                <Button type="button" variant="outline" onClick={() => document.getElementById('client-video-upload').click()}>
                  <Upload className="w-4 h-4" />
                </Button>
                <input id="client-video-upload" type="file" accept="video/*" className="hidden" onChange={(e) => handleSimpleUpload(e, 'client_video_url')} />
              </div>
              {formData.client_video_url && (
                <div className="mt-4 space-y-4">
                  <MediaDisplay type="video" url={formData.client_video_url} meta={formData.client_video_meta || {}} controls className="h-32 rounded object-contain" />
                  <div><Label>Titolo SEO video *</Label><Input value={formData.client_video_meta?.title || ''} onChange={(e) => setFormData(prev => ({ ...prev, client_video_meta: { ...(prev.client_video_meta || {}), title: e.target.value } }))} required /></div>
                  <div><Label>Descrizione SEO video *</Label><Textarea value={formData.client_video_meta?.description || formData.client_video_description || ''} onChange={(e) => setFormData(prev => ({ ...prev, client_video_meta: { ...(prev.client_video_meta || {}), description: e.target.value } }))} rows={3} required /></div>
                  <div><Label>Parole chiave SEO video</Label><Input value={formData.client_video_meta?.tags || ''} onChange={(e) => setFormData(prev => ({ ...prev, client_video_meta: { ...(prev.client_video_meta || {}), tags: e.target.value } }))} placeholder="architettura, testimonianza cliente, progetto" /></div>
                </div>
              )}
            </div>

            {/* AI SEO Suggestions */}
            <AISeoSuggestions
              formData={formData}
              acceptedFields={acceptedFields}
              onFieldAccepted={(fieldKey) => {
                const updated = { ...acceptedFields, [fieldKey]: true };
                setAcceptedFields(updated);
                saveAcceptedFields(editing, updated);
              }}
              onApply={(fieldKey, value) => {
                const normalizedValue = Array.isArray(value) ? value.join(', ') : value;
                const parts = fieldKey.split('.');
                // gallery_images_meta.N.field
                if (parts.length === 3 && parts[0] === 'gallery_images_meta') {
                  const idx = parseInt(parts[1], 10);
                  const child = parts[2];
                  setFormData(prev => {
                    const metas = [...(prev.gallery_images_meta || [])];
                    metas[idx] = { ...(metas[idx] || {}), [child]: normalizedValue };
                    return { ...prev, gallery_images_meta: metas };
                  });
                } else if (parts.length === 2) {
                  const [parent, child] = parts;
                  setFormData(prev => ({
                    ...prev,
                    [parent]: { ...(prev[parent] || {}), [child]: normalizedValue }
                  }));
                } else {
                  setFormData(prev => ({ ...prev, [fieldKey]: normalizedValue }));
                }
              }}
            />

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={activeUploads > 0 || createMutation.isPending || updateMutation.isPending}>
                {activeUploads > 0 ? `Caricamento immagini (${activeUploads})...` : createMutation.isPending || updateMutation.isPending ? 'Salvataggio...' : 'Salva'}
              </Button>
              <Button type="button" variant="outline" disabled={activeUploads > 0} onClick={() => setEditing(null)}>Annulla</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-serif">Progetti</h2>
        <Button onClick={handleNew}>
          <Plus className="w-4 h-4 mr-2" /> Nuovo Progetto
        </Button>
      </div>
      <div className="grid gap-4">
        {projects.map((project) => (
          <Card key={project.id}>
            <CardContent className="flex items-center gap-4 p-4">
              {((project.cover_media_type || 'image') === 'video' ? project.cover_video_url : project.cover_image) && (
                <div className="pointer-events-none h-24 w-24 overflow-hidden rounded"><MediaDisplay type={project.cover_media_type || 'image'} url={(project.cover_media_type || 'image') === 'video' ? project.cover_video_url : project.cover_image} meta={project.cover_image_meta || { title: project.title }} autoplay={(project.cover_media_type || 'image') === 'video'} className="h-full w-full object-cover" /></div>
              )}
              <div className="flex-1">
                <h3 className="font-medium">{project.title}</h3>
                <p className="text-sm text-[#7a7a7a]">{project.category} • {project.year} • {project.location}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => handleEdit(project)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => { if (confirm('Eliminare questo progetto?')) deleteMutation.mutate(project.id); }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}